import React, { useMemo } from "react";
import TailwindButton from "library/components/_tailwind/button";
import { IShowType, ShowTab } from "common/broadcast/_stores/broadcast/enums";
import BroadcastStore from "common/broadcast/_stores/broadcast/BroadcastStore";
import { inject, observer } from "mobx-react";
import PricingStore from "common/broadcast/_stores/pricing/PricingStore";
import LanguageStore from "library/core/stores/language/LanguageStore";

const SHOW_TAB_LABEL_KEYS: Record<ShowTab, { id: string; defaultMessage: string }> = {
  [ShowTab.FREE]: {
    id: "broadcast-show-type-control.free.item-title",
    defaultMessage: "FREE",
  },
  [ShowTab.NUDE]: { id: "broadcast.nudeTab", defaultMessage: "Nude" },
  [ShowTab.TIPPING]: {
    id: "broadcast-show-type-control.tipping.item-title",
    defaultMessage: "TIPPING",
  },
};

type Props = {
  broadcastStore?: BroadcastStore;
  pricingStore?: PricingStore;
  languageStore?: LanguageStore;
};

const BroadcastSwitchShowButton: React.ComponentType<Props> = ({
  broadcastStore,
  pricingStore,
  languageStore
}) => {
  const { intl } = languageStore!;
  const {
    isShowStarted,
    currentShowTab,
    currentShowType,
    switchToShow,
    isStartingOrStoppingStream,
    isInAdminShow,
  } = broadcastStore!;
  const { areTippingFieldsValid } = pricingStore!;

  const shouldShowSwitch = useMemo(() => {
    return (
      (currentShowTab === ShowTab.FREE &&
        ![IShowType.FREE, IShowType.PRIVATE, IShowType.GROUP].includes(
          currentShowType
        )) ||
      (currentShowTab === ShowTab.TIPPING &&
        ![
          IShowType.CURTAIN_DROPPED,
          IShowType.TIPPING,
          IShowType.GROUP,
        ].includes(currentShowType)) ||
      (currentShowTab === ShowTab.NUDE &&
        ![IShowType.NUDE, IShowType.GROUP].includes(currentShowType))
    );
  }, [currentShowType, currentShowTab]);

  const localizedShowTab = useMemo(() => {
    const descriptor = SHOW_TAB_LABEL_KEYS[currentShowTab];
    return descriptor ? intl.formatMessage(descriptor) : currentShowTab;
  }, [currentShowTab, intl]);

  if (!isShowStarted || !shouldShowSwitch || isStartingOrStoppingStream) {
    return null;
  }

  return (
    <TailwindButton
      data-testid={`broadcast-switch-show-button-${currentShowTab}`}
      settings={true}
      disabled={!areTippingFieldsValid || isInAdminShow}
      className={["mt-4", "flex-1", "min-w-0"]}
      backgroundColor={"secondary"}
      rounded={false}
      fullWidth={true}
      padding={["px-4", "py-2"]}
      textProps={{ textAlign: "text-center" }}
      justifyContent={"justify-center"}
      onClick={() => switchToShow(currentShowTab)}>
      {intl.formatMessage(
        {
          id: "broadcast-control-button.switch",
          defaultMessage: "Switch to {activeShowTab} show",
        },
        {
          activeShowTab: localizedShowTab,
        }
      )}
    </TailwindButton>
  );
};

export default inject(
  "broadcastStore",
  "pricingStore",
  "languageStore"
)(observer(BroadcastSwitchShowButton));
